import React, { useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { META, generateTermsRichSnippet } from "./meta";
import { animateTitle, animateContent, cleanupScrollTriggers } from "./gsap";
import {
  BUSINESS_NAME,
  BUSINESS_CONTACT_EMAIL,
  BUSINESS_CONTACT_PHONE,
} from "@/core/constants/business";

const TermsOfService: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Rich snippet data for SEO
  const richSnippet = generateTermsRichSnippet();

  useEffect(() => {
    // Apply animations after a small delay to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && titleRef.current && contentRef.current) {
        animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );
        animateContent(
          { current: contentRef.current },
          { current: sectionRef.current }
        );
      }
    }, 100);

    // Clean up animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>{META.title}</title>
        <meta name="description" content={META.meta_description} />
        <meta name="keywords" content={META.keywords} />
        <link
          rel="canonical"
          href={`https://slsdispensary.com${META.canonicalUrl}`}
        />

        {/* OpenGraph Meta Tags */}
        <meta property="og:title" content={META.title} />
        <meta property="og:description" content={META.meta_description} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://slsdispensary.com${META.canonicalUrl}`}
        />
        <meta
          property="og:image"
          content="https://slsdispensary.com/images/street-lawyer-services-dc-dispensary-entrance.jpg"
        />
        <meta property="og:site_name" content={BUSINESS_NAME} />

        {/* JSON-LD for rich snippets */}
        <script type="application/ld+json">
          {JSON.stringify(richSnippet)}
        </script>
      </Helmet>

      {/* Main Content */}
      <section ref={sectionRef} className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h1
            ref={titleRef}
            className="text-4xl md:text-5xl font-bold mb-8 text-center"
          >
            Terms of Service
          </h1>

          <div
            ref={contentRef}
            className="max-w-3xl mx-auto prose prose-lg terms-content"
          >
            <p className="text-xl text-gray-700 mb-8 terms-intro">
              Welcome to {BUSINESS_NAME}. These Terms of Service govern your use
              of our website and services. By accessing our website or
              purchasing our products, you agree to be bound by these terms and
              conditions.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">1. Introduction</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding
              agreement between you, the user, and {BUSINESS_NAME} ("we," "us,"
              or "our") regarding your access to and use of our website,
              services, and products.
            </p>
            <p>
              By accessing or using our website, purchasing our products, or
              engaging with our services, you acknowledge that you have read,
              understood, and agree to be bound by these Terms. If you do not
              agree to these Terms, please do not use our website or services.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              2. Age Restrictions
            </h2>
            <p>
              Our website and services are strictly intended for adults 21 years
              of age or older. By accessing our website or purchasing our
              products, you represent and warrant that you are at least 21 years
              old.
            </p>
            <p>
              We reserve the right to request proof of age at any time, and to
              refuse service to anyone who cannot provide valid identification
              demonstrating they are at least 21 years of age.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              3. Compliance with Laws
            </h2>
            <p>
              You agree to comply with all applicable federal, state, and local
              laws, regulations, and ordinances while using our website and
              services. Our products are intended for consumption only in
              jurisdictions where such use is legal.
            </p>
            <p>
              While cannabis is legal under District of Columbia law, it remains
              illegal under federal law. By using our services, you acknowledge
              this conflict and assume all risks associated with the purchase
              and possession of cannabis products.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">4. Website Use</h2>
            <p>
              The content on our website is for informational purposes only. We
              strive to provide accurate information about our products, but we
              make no warranties or representations regarding the accuracy,
              reliability, or completeness of this information.
            </p>
            <p>You agree not to:</p>
            <ul>
              <li>Use our website for any illegal purpose</li>
              <li>
                Attempt to gain unauthorized access to our website or systems
              </li>
              <li>Interfere with or disrupt the operation of our website</li>
              <li>
                Collect or harvest any information from our website without our
                express consent
              </li>
              <li>
                Use our website to transmit any viruses, malware, or other
                harmful code
              </li>
            </ul>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              5. Products and Services
            </h2>
            <p>
              All products displayed on our website are subject to availability.
              We reserve the right to discontinue any product at any time and to
              limit quantities sold to any customer.
            </p>
            <p>
              Product images on our website are for illustrative purposes only.
              Actual products may vary in appearance, packaging, or content.
            </p>
            <p>
              We make every effort to display product information accurately,
              but we do not warrant that product descriptions or other content
              on our website are accurate, complete, reliable, current, or
              error-free.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              6. Purchases and Payment
            </h2>
            <p>
              All sales are final. Please refer to our{" "}
              <a
                href="/refund-policy"
                className="text-cannabis-green-600 hover:text-cannabis-green-700"
              >
                Refund Policy
              </a>{" "}
              for additional information.
            </p>
            <p>
              By making a purchase through our website or at our dispensary, you
              represent and warrant that:
            </p>
            <ul>
              <li>You are at least 21 years of age</li>
              <li>The information you provide is accurate and complete</li>
              <li>
                You have the legal right to use the payment method you provide
              </li>
            </ul>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              7. Intellectual Property
            </h2>
            <p>
              All content on our website, including but not limited to text,
              graphics, logos, images, audio clips, digital downloads, and data
              compilations, is the property of {BUSINESS_NAME} or its content
              suppliers and is protected by United States and international
              copyright laws.
            </p>
            <p>
              You may not use, reproduce, distribute, modify, or create
              derivative works from any content without our express written
              permission.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">8. Privacy</h2>
            <p>
              Your use of our website and services is also governed by our{" "}
              <a
                href="/privacy-policy"
                className="text-cannabis-green-600 hover:text-cannabis-green-700"
              >
                Privacy Policy
              </a>
              . Please review this policy to understand how we collect, use, and
              protect your personal information.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              9. Disclaimer of Warranties
            </h2>
            <p>
              OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE,"
              WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO
              THE FULLEST EXTENT PERMISSIBLE UNDER APPLICABLE LAW, WE DISCLAIM
              ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
              IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              WE DO NOT WARRANT THAT OUR WEBSITE OR SERVICES WILL BE
              UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR
              THAT OUR WEBSITE OR THE SERVERS THAT MAKE IT AVAILABLE ARE FREE OF
              VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              10. Limitation of Liability
            </h2>
            <p>
              TO THE FULLEST EXTENT PERMISSIBLE UNDER APPLICABLE LAW,{" "}
              {BUSINESS_NAME} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
              PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR
              ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES,
              RESULTING FROM:
            </p>
            <ul>
              <li>
                YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE OUR
                WEBSITE OR SERVICES
              </li>
              <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON OUR WEBSITE</li>
              <li>ANY CONTENT OBTAINED FROM OUR WEBSITE</li>
              <li>
                UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR
                CONTENT
              </li>
            </ul>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              11. Indemnification
            </h2>
            <p>
              You agree to defend, indemnify, and hold harmless {BUSINESS_NAME},
              its affiliates, and their respective officers, directors,
              employees, and agents, from and against any and all claims,
              damages, obligations, losses, liabilities, costs, or debt, and
              expenses (including but not limited to attorney's fees) arising
              from:
            </p>
            <ul>
              <li>Your use of and access to our website and services</li>
              <li>Your violation of any term of these Terms</li>
              <li>
                Your violation of any third-party right, including without
                limitation any copyright, property, or privacy right
              </li>
              <li>
                Any claim that your use of our website or services caused damage
                to a third party
              </li>
            </ul>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              12. Modifications to Terms
            </h2>
            <p>
              We reserve the right to modify these Terms at any time. All
              changes are effective immediately when we post them and apply to
              all access to and use of our website thereafter.
            </p>
            <p>
              Your continued use of our website following the posting of revised
              Terms means that you accept and agree to the changes. You are
              expected to check this page frequently so you are aware of any
              changes.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">13. Termination</h2>
            <p>
              We reserve the right to terminate or suspend your access to our
              website and services immediately, without prior notice or
              liability, for any reason whatsoever, including without limitation
              if you breach these Terms.
            </p>
            <p>
              Upon termination, your right to use our website and services will
              immediately cease. All provisions of these Terms which by their
              nature should survive termination shall survive termination.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">14. Governing Law</h2>
            <p>
              These Terms shall be governed and construed in accordance with the
              laws of the District of Columbia, without regard to its conflict
              of law provisions.
            </p>
            <p>
              Any legal suit, action, or proceeding arising out of, or related
              to, these Terms or our website shall be instituted exclusively in
              the courts located in the District of Columbia.
            </p>

            <h2 className="text-3xl font-bold mt-12 mb-6">
              15. Contact Information
            </h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              {BUSINESS_NAME}
              <br />
              3400 Connecticut Avenue NW
              <br />
              Washington, DC 20008
              <br />
              Phone: {BUSINESS_CONTACT_PHONE}
              <br />
              Email: {BUSINESS_CONTACT_EMAIL}
            </p>

            <p className="text-sm text-gray-500 mt-8 italic">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;
